import {
  AvatarGroup,
  AvatarGroupItem,
  createTableColumn,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  TableCellLayout,
  TableColumnDefinition,
} from '@fluentui/react-components'
import { ClipboardTextLtr24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { DataGridComponent } from '@/ui/DataGrid/DataGridComponent'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'
import { OrganisationProjectForm } from '@/ui/Forms/OrganisationProjectForm'
import { RowActionsMenu } from './RowActionsMenu'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isSuperUser } from '@/util'

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']

export const OrganisationProjectsPanel = () => {
  const [openAddOrgProjectDrawer, setOpenAddOrgProjectDrawer] = useState(false)
  const orgId = useGetSearchParam<number>('orgId')
  const { getProjectsApiListOrganisationProjects } = createRestClient()

  const [userRole] = useSessionStorage('roles', 1)
  const [isAddProjectAllowed] = useState(() => {
    return isPlatformAdmin(userRole) || isSuperUser(userRole)
  })

  const {
    data: organisationProjects,
    isPending,
    isError,
  } = getProjectsApiListOrganisationProjects.useQuery(
    {
      query: {
        orgId,
      },
    },
    {
      enabled: !!orgId,
    }
  )

  // TODO: Add error handling and loading state
  if (isPending) return null
  if (isError) return null

  const columns: TableColumnDefinition<MessagesProjectsOrganisationProject>[] = [
    createTableColumn<MessagesProjectsOrganisationProject>({
      columnId: 'name',
      compare: (a, b) => {
        return a.name.localeCompare(b.name)
      },
      renderHeaderCell: () => {
        return 'Name'
      },
      renderCell: (item) => {
        return (
          <TableCellLayout media={<ClipboardTextLtr24Regular color={!item.isFrozen ? '#D04A02' : '#D1D1D1'} />}>
            <div
              style={{
                flexDirection: 'column',
                display: 'flex',
                paddingTop: '.5rem',
                paddingBottom: '.5rem',
                gap: '5px',
                lineHeight: '1rem',
              }}
            >
              <span style={{ fontSize: '16px' }}>{item.name}</span>
            </div>
          </TableCellLayout>
        )
      },
    }),
    createTableColumn<MessagesProjectsOrganisationProject>({
      columnId: 'workspaceCount',
      compare: (a, b) => {
        return a.workspaceCount - b.workspaceCount
      },
      renderHeaderCell: () => {
        return 'Workspaces'
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.workspaceCount.toString()}</TableCellLayout>
      },
    }),
    createTableColumn<MessagesProjectsOrganisationProject>({
      columnId: 'admins',
      compare: (a, b) => {
        return a.administrators.length - b.administrators.length
      },
      renderHeaderCell: () => {
        return 'Admins'
      },
      renderCell: (item) => {
        return (
          <TableCellLayout>
            <AvatarGroup layout="stack">
              {item.administrators.map((a) => (
                <Popover key={a.name}>
                  <PopoverTrigger disableButtonEnhancement>
                    <AvatarGroupItem name={`${a.name}`} key={a.name} />
                  </PopoverTrigger>
                  <PopoverSurface tabIndex={-1}>
                    {a.name} ({a.organisation})
                  </PopoverSurface>
                </Popover>
              ))}
            </AvatarGroup>
          </TableCellLayout>
        )
      },
    }),
    createTableColumn<MessagesProjectsOrganisationProject>({
      columnId: 'leaders',
      compare: (a, b) => {
        return a.leaders.length - b.leaders.length
      },
      renderHeaderCell: () => {
        return 'Leaders'
      },
      renderCell: (item) => {
        return (
          <TableCellLayout>
            <AvatarGroup layout="stack">
              {item.leaders.map((a) => (
                <Popover key={a.name}>
                  <PopoverTrigger disableButtonEnhancement>
                    <AvatarGroupItem name={`${a.name}`} key={a.name} />
                  </PopoverTrigger>
                  <PopoverSurface tabIndex={-1}>
                    {a.name} ({a.organisation})
                  </PopoverSurface>
                </Popover>
              ))}
            </AvatarGroup>
          </TableCellLayout>
        )
      },
    }),
    createTableColumn<MessagesProjectsOrganisationProject>({
      columnId: 'actions',
      renderHeaderCell: () => {
        return 'Actions'
      },
      renderCell: (item) => {
        return <RowActionsMenu item={item} />
      },
    }),
  ]

  return (
    <>
      <DataGridComponent
        items={organisationProjects}
        columns={columns}
        heightAdjustment={90}
        onAdd={isAddProjectAllowed ? () => setOpenAddOrgProjectDrawer(true) : undefined}
      />
      <OverlayDrawerComponent
        open={openAddOrgProjectDrawer}
        setOpen={setOpenAddOrgProjectDrawer}
        title={'Add project to organisation'}
        content={<OrganisationProjectForm onSubmit={() => setOpenAddOrgProjectDrawer(false)} />}
      />
    </>
  )
}
